import type { ReactNode } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import { Ban, Camera, Layers3, MapPinned, Palette, ScanLine, Shapes, Sparkles } from "lucide-react";

import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@runcheetah/ui";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type VisualUniverse = NonNullable<
  RouterOutputs["admin"]["brands"]["visualUniverse"]["visualUniverse"]
>;
type IconComponent = typeof Sparkles;

function SectionCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: IconComponent;
  children: ReactNode;
}) {
  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className="border-b bg-muted/20">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-4">{children}</CardContent>
    </Card>
  );
}

function ChipList({ items }: { items: readonly string[] }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <span key={item} className="rounded-full border bg-background px-2.5 py-1 text-xs">
          {item}
        </span>
      ))}
    </div>
  );
}

function LabelBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </div>
      <div className="text-sm leading-6 text-foreground/85">{children}</div>
    </div>
  );
}

function TextList({ items }: { items: readonly string[] }) {
  return (
    <ol className="space-y-2">
      {items.map((item, index) => (
        <li key={`${item}-${index}`} className="flex gap-2 rounded-xl border bg-background p-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
            {index + 1}
          </span>
          <span className="text-sm leading-6 text-muted-foreground">{item}</span>
        </li>
      ))}
    </ol>
  );
}

function SmallCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <article className="space-y-3 rounded-2xl border bg-background p-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </article>
  );
}

export function VisualUniverseSection({ brandId }: { brandId: string }) {
  const query = trpc.admin.brands.visualUniverse.useQuery(
    { brandId },
    { refetchOnWindowFocus: true, staleTime: 0 },
  );

  if (query.isPending) {
    return <Skeleton className="h-80" />;
  }

  if (query.error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Visual Universe</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-destructive">{query.error.message}</CardContent>
      </Card>
    );
  }

  const universe = query.data?.visualUniverse ?? null;
  if (!universe) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Visual Universe</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No visual universe facts exist for this brand yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <VisualUniverseContent universe={universe} generatedAt={query.data?.generatedAt ?? null} />
  );
}

function VisualUniverseContent({
  universe,
  generatedAt,
}: {
  universe: VisualUniverse;
  generatedAt: string | null;
}) {
  return (
    <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
      <Card className="min-w-0 overflow-hidden xl:col-span-2">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <CardTitle className="text-base font-semibold">Visual Universe</CardTitle>
            {generatedAt ? (
              <Badge variant="secondary">Generated {formatDateTime(generatedAt)}</Badge>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="grid gap-5 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.55fr)]">
          <p className="text-lg font-medium leading-8">{universe.summary}</p>
          <div className="space-y-3 rounded-2xl border bg-background p-4">
            <h3 className="text-sm font-semibold">Source logic</h3>
            <TextList items={universe.sourceLogic} />
          </div>
        </CardContent>
      </Card>

      <SectionCard title="Photography Archetypes" icon={Camera}>
        <div className="grid gap-3 lg:grid-cols-2">
          {universe.photographyArchetypes.map((item) => (
            <SmallCard key={item.name} title={item.name}>
              <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
              <LabelBlock label="Subjects">
                <ChipList items={item.subjects} />
              </LabelBlock>
              <LabelBlock label="Settings">
                <ChipList items={item.settings} />
              </LabelBlock>
              <LabelBlock label="Lighting">{item.lighting}</LabelBlock>
              <LabelBlock label="Use when">{item.useWhen}</LabelBlock>
            </SmallCard>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Composition Rules" icon={ScanLine}>
        <TextList items={universe.compositionRules} />
      </SectionCard>

      <SectionCard title="Environments" icon={MapPinned}>
        <div className="grid gap-3 lg:grid-cols-2">
          {universe.environments.map((item) => (
            <SmallCard key={item.name} title={item.name}>
              <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
              <ChipList items={item.cues} />
            </SmallCard>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Materials" icon={Layers3}>
        <div className="grid gap-3 lg:grid-cols-2">
          {universe.materials.map((item) => (
            <SmallCard key={item.name} title={item.name}>
              <p className="text-sm leading-6 text-muted-foreground">{item.usage}</p>
              <LabelBlock label="Sensory cue">{item.sensoryCue}</LabelBlock>
            </SmallCard>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Motifs" icon={Shapes}>
        <div className="grid gap-3 lg:grid-cols-2">
          {universe.motifs.map((item) => (
            <SmallCard key={item.name} title={item.name}>
              <p className="text-sm leading-6 text-muted-foreground">{item.usage}</p>
              <LabelBlock label="Meaning">{item.meaning}</LabelBlock>
            </SmallCard>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Color Roles" icon={Palette}>
        <div className="grid gap-3 lg:grid-cols-2">
          {universe.colorRoles.map((item) => (
            <SmallCard key={item.name} title={item.name}>
              <p className="text-sm leading-6 text-muted-foreground">{item.role}</p>
              <ChipList items={item.colors} />
              <LabelBlock label="Usage guidance">{item.usageGuidance}</LabelBlock>
            </SmallCard>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Prompt Patterns" icon={Sparkles}>
        <div className="grid gap-3 lg:grid-cols-2">
          {universe.promptPatterns.map((item) => (
            <SmallCard key={item.name} title={item.name}>
              <LabelBlock label="Pattern">
                <code className="rounded-md bg-muted px-2 py-1 text-xs">{item.pattern}</code>
              </LabelBlock>
              <LabelBlock label="Example">{item.example}</LabelBlock>
              <LabelBlock label="Use when">{item.useWhen}</LabelBlock>
            </SmallCard>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Anti-patterns" icon={Ban}>
        <TextList items={universe.antiPatterns} />
      </SectionCard>
    </div>
  );
}
